/**
 * editor2/modals/ConflictDialog.tsx
 * 保存時に「自分も他の端末も同じフィールドを変更していた」場合の確認ダイアログ。
 *
 * 列: パネルを開いた時 / あなたの変更 / 最新（他の端末）
 * 行ごとに「自分の値で上書き」「最新の値を残す」を選ぶ。既定は「自分の値で上書き」
 * （利用者は保存ボタンを押した＝自分の値を書きたい、が出発点）。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type { FieldConflict, Resolution, StatusLike } from '../conflict/threeWayMerge';
import { formatFieldValue } from '../conflict/threeWayMerge';
import { MARK_COLORS } from '../conflict/fieldState';

export interface ConflictDialogProps {
  open: boolean;
  conflicts: FieldConflict[];
  /** サーバー最新の updated_at（表示用） */
  theirsUpdatedAt?: string | null;
  /** 相手がメモに追記した内容（誰が何をしたかの手掛かり）。無ければ非表示 */
  theirMemoAdded?: string | null;
  statusOptions?: StatusLike[];
  onResolve: (resolutions: Record<string, Resolution>) => void;
  onCancel: () => void;
}

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function ConflictDialog({
  open,
  conflicts,
  theirsUpdatedAt,
  theirMemoAdded,
  statusOptions,
  onResolve,
  onCancel,
}: ConflictDialogProps) {
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({});

  // 開くたびに既定（mine）へ戻す
  useEffect(() => {
    if (open) {
      const init: Record<string, Resolution> = {};
      conflicts.forEach((c) => { init[c.field] = 'mine'; });
      setResolutions(init);
    }
  }, [open, conflicts]);

  const counts = useMemo(() => {
    let mine = 0;
    let theirs = 0;
    conflicts.forEach((c) => { if (resolutions[c.field] === 'theirs') theirs += 1; else mine += 1; });
    return { mine, theirs };
  }, [conflicts, resolutions]);

  const setAll = (r: Resolution) => {
    const next: Record<string, Resolution> = {};
    conflicts.forEach((c) => { next[c.field] = r; });
    setResolutions(next);
  };

  const cell: React.CSSProperties = {
    padding: '6px 8px',
    fontSize: '0.85rem',
    verticalAlign: 'top',
    borderBottom: '1px solid #e2e8f0',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  };
  const head: React.CSSProperties = {
    ...cell,
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#475569',
    background: '#f8fafc',
    whiteSpace: 'nowrap',
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ color: MARK_COLORS.conflict.border }}>⚠</span>
        他の端末で先に更新されています
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          パネルを開いた後に、下の項目が別の端末で変更されました
          {theirsUpdatedAt ? `（最終更新 ${fmtTime(theirsUpdatedAt)}）` : ''}。
          あなたも同じ項目を変更しているため、どちらの値を保存するか選んでください。
          ここに出ていない項目は、あなたの変更と相手の変更の両方がそのまま保存されます。
        </Typography>

        {theirMemoAdded && (
          <Box
            sx={{
              mb: 1.5,
              p: 1,
              border: `1px solid ${MARK_COLORS.remote.border}`,
              background: MARK_COLORS.remote.bg,
              borderRadius: 1,
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              maxHeight: 140,
              overflow: 'auto',
            }}
          >
            <div style={{ fontWeight: 700, color: MARK_COLORS.remote.text, marginBottom: 4 }}>相手側の変更記録（メモ）</div>
            {theirMemoAdded}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 1, justifyContent: 'flex-end' }}>
          <Button size="small" variant="outlined" onClick={() => setAll('mine')}>すべて自分の値</Button>
          <Button size="small" variant="outlined" onClick={() => setAll('theirs')}>すべて最新の値</Button>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>項目</th>
                <th style={{ ...head, textAlign: 'left' }}>開いた時</th>
                <th style={{ ...head, textAlign: 'left', color: MARK_COLORS.dirty.text }}>あなたの変更</th>
                <th style={{ ...head, textAlign: 'left', color: MARK_COLORS.remote.text }}>最新（他の端末）</th>
                <th style={{ ...head, textAlign: 'center' }}>保存する値</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((c) => {
                const r = resolutions[c.field] || 'mine';
                return (
                  <tr key={c.field}>
                    <td style={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.label}</td>
                    <td style={{ ...cell, color: '#64748b' }}>{formatFieldValue(c.field, c.base, statusOptions)}</td>
                    <td
                      style={{
                        ...cell,
                        background: r === 'mine' ? MARK_COLORS.dirty.bg : undefined,
                        fontWeight: r === 'mine' ? 600 : 400,
                      }}
                    >
                      {formatFieldValue(c.field, c.mine, statusOptions)}
                    </td>
                    <td
                      style={{
                        ...cell,
                        background: r === 'theirs' ? MARK_COLORS.remote.bg : undefined,
                        fontWeight: r === 'theirs' ? 600 : 400,
                      }}
                    >
                      {formatFieldValue(c.field, c.theirs, statusOptions)}
                    </td>
                    <td style={{ ...cell, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={r}
                        onChange={(_e, v) => { if (v) setResolutions((prev) => ({ ...prev, [c.field]: v })); }}
                      >
                        <ToggleButton value="mine" sx={{ fontSize: '0.72rem', px: 1 }}>自分の値</ToggleButton>
                        <ToggleButton value="theirs" sx={{ fontSize: '0.72rem', px: 1 }}>最新の値</ToggleButton>
                      </ToggleButtonGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>保存を中止</Button>
        <Button variant="contained" onClick={() => onResolve(resolutions)}>
          この内容で保存（自分の値 {counts.mine} / 最新の値 {counts.theirs}）
        </Button>
      </DialogActions>
    </Dialog>
  );
}
